import { PrizeTier } from '../types';
import { getPrizeConfig } from '../../../services/quizApi';

/**
 * KBC Prize Tiers Configuration
 * 5 tiers matching 5 difficulty levels
 * Each tier represents a milestone in the quiz
 * 
 * NOTE: Prize config is now fetched from the backend API.
 * The backend reads from prize_config.json which is the single source of truth.
 * No localStorage caching - all users see the same prizes as configured by admins.
 */

// Default prize tiers (fallback only if API fails)
const DEFAULT_PRIZE_TIERS: PrizeTier[] = [
  { level: 1, label: 'Shipment CoCaptain Coaster', value: 0, enabled: true },
  { level: 2, label: 'Maersk x Shipment CoCaptain Mug', value: 0, enabled: true },
  { level: 3, label: 'Maersk x Shipment CoCaptain Temp Sensor Bottle', value: 0, enabled: true },
  { level: 4, label: 'Maersk x Shipment CoCaptain TShirt', value: 0, enabled: true },
  { level: 5, label: 'Grand Prize - Maersk x Shipment CoCaptain Box Set', value: 0, enabled: true },
];

// In-memory cache for current session (no localStorage)
let cachedPrizeTiers: PrizeTier[] | null = null;

/**
 * Fetch prize tiers from backend API
 * This is the source of truth - no localStorage caching
 */
export const fetchPrizeTiers = async (): Promise<PrizeTier[]> => {
  try {
    const tiers = await getPrizeConfig();
    cachedPrizeTiers = tiers; // Cache for current session only
    return tiers;
  } catch (error) {
    console.error('Failed to fetch prize config from API, using defaults:', error);
    return [...DEFAULT_PRIZE_TIERS];
  }
};

/**
 * Get current prize tiers (from session cache or defaults)
 * Components should call fetchPrizeTiers() on mount to get latest from API
 */
export const getPrizeTiers = (): PrizeTier[] => {
  return cachedPrizeTiers ? [...cachedPrizeTiers] : [...DEFAULT_PRIZE_TIERS];
};

/**
 * Update all prize tiers in memory (used by admin panel after successful API update)
 */
export const updateAllPrizeTiers = (newTiers: PrizeTier[]): void => {
  if (newTiers.length === 5) {
    cachedPrizeTiers = [...newTiers];
  }
};

/**
 * Get prize details for a specific level
 */
export const getPrizeForLevel = (level: number): PrizeTier | undefined => {
  const tiers = getPrizeTiers();
  return tiers.find((prize: PrizeTier) => prize.level === level);
};

/**
 * Get the current tier based on questions answered correctly
 * Tiers are awarded only when completed (4 questions per tier)
 * @param correctAnswers - Number of questions answered correctly
 * @returns Current tier level (0-5)
 */
export const getTierFromCorrectAnswers = (correctAnswers: number): number => {
  // Each tier requires 4 correct answers
  // 0-3 correct = Tier 0 (no prize)
  // 4-7 correct = Tier 1
  // 8-11 correct = Tier 2
  // 12-15 correct = Tier 3
  // 16-19 correct = Tier 4
  // 20 correct = Tier 5
  return Math.floor(correctAnswers / QUIZ_CONFIG.questionsPerTier);
};

/**
 * Get the prize for a given number of correct answers
 */
export const getPrizeForCorrectAnswers = (correctAnswers: number): PrizeTier | undefined => {
  const tier = getTierFromCorrectAnswers(correctAnswers);
  return getPrizeForLevel(tier);
};

/**
 * Check if a tier is enabled for cash out
 */
export const isPrizeTierEnabled = (level: number): boolean => {
  const prize = getPrizeForLevel(level);
  return prize?.enabled ?? false;
};

/**
 * Get the next enabled prize tier after the current one
 */
export const getNextEnabledTier = (currentLevel: number): number | null => {
  const tiers = getPrizeTiers();
  const nextPrize = tiers.find(
    (prize: PrizeTier) => prize.level > currentLevel && prize.enabled
  );
  return nextPrize?.level ?? null;
};

/**
 * Maersk brand colors
 */
export const COLORS = {
  maerskBlue: '#42B0D5',
  maerskNavy: '#00243D',
  maerskAccent: '#00A9CE',
  kbcGold: '#FFD700',
  kbcAmber: '#FFA500',
  success: '#4CAF50',
  error: '#FF6B35',
};

/**
 * Sound effect paths (placeholders)
 */
export const SOUNDS = {
  questionSting: '/sounds/question.wav',
  correct: '/sounds/correct.wav',
  wrong: '/sounds/wrong.wav',
  cashout: '/sounds/cashout.wav',
  victory: '/sounds/victory.wav',
  background: '/sounds/background.wav',
};

/**
 * Quiz app configuration
 */
export const QUIZ_CONFIG = {
  totalQuestions: 20, // 20 questions per quiz session
  questionsPerTier: 4, // 4 questions per difficulty tier (5 tiers × 4 = 20)
  timePerQuestion: 60, // seconds (optional timer)
  leaderboardRefreshInterval: 30000, // 30 seconds
  maxLeaderboardEntries: 50,
};
